<template>
    <card>
        <div :class="$style.header">
            <h3 class="mb-0">
                Unit #{{ preOrderUnit.unitId }}, Pre-Order #{{
                    preOrderUnit.id
                }}
            </h3>
            <base-button
                id="edit"
                type="primary"
                :disabled="isEditing"
                class="btn-fill"
                size="sm"
                @click="isEditing = true"
            >
                <i class="fa fa-edit" /> Edit
            </base-button>
        </div>
        <ValidationObserver ref="form" v-slot="{ invalid }">
            <form
                :class="isEditing ? '' : 'view-only-form'"
                autocomplete="off"
                @keydown.enter.prevent=""
                @submit.prevent="updateUnit"
            >
                <div class="row">
                    <div class="col-md-6">
                        <base-input
                            id="description"
                            v-model="preOrderUnitEdit.description"
                            name="description"
                            label="Description"
                            :disabled="!isEditing"
                        />
                    </div>
                    <div class="col-md-2">
                        <div class="form-group has-label">
                            <label>Length (in.)</label>
                            <masked-input
                                v-model="length"
                                class="form-control"
                                name="length"
                                type="text"
                                :mask="numberMask"
                                :error="errors[0]"
                                :disabled="!isEditing"
                            />
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="form-group has-label">
                            <label>Width (in.)</label>
                            <masked-input
                                v-model="width"
                                class="form-control"
                                name="width"
                                type="text"
                                :mask="numberMask"
                                :error="errors[0]"
                                :disabled="!isEditing"
                            />
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="form-group has-label">
                            <label>Height (in.)</label>
                            <masked-input
                                v-model="height"
                                class="form-control"
                                name="height"
                                type="text"
                                :mask="numberMask"
                                :error="errors[0]"
                                :disabled="!isEditing"
                            />
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <label>Manufacturer</label>
                        <v-select
                            v-model="preOrderUnitEdit.manufacturerId"
                            :reduce="(manufacturer) => manufacturer.id"
                            :options="manufacturerOptions"
                            placeholder="Select manufacturer"
                            label="name"
                            :disabled="!isEditing"
                            @search="search(...arguments)"
                        >
                            <template slot="no-options">
                                <span v-if="searchText">No results found.</span>
                                <span v-else>Type to search...</span>
                            </template>
                            <template slot="option" slot-scope="option">
                                {{ option.name }}
                            </template>
                        </v-select>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            id="relatedNumber"
                            v-model="preOrderUnitEdit.relatedNumber"
                            name="relatedNumber"
                            type="text"
                            label="S/N"
                            :disabled="!isEditing"
                        />
                    </div>
                    <div class="col-md-3">
                        <base-input
                            id="trackingNumber"
                            v-model="preOrderUnitEdit.trackingNumber"
                            name="trackingNumber"
                            type="text"
                            label="Tracking No."
                            :disabled="!isEditing"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3">
                        <base-input
                            id="itemNumber"
                            v-model="preOrderUnitEdit.itemNumber"
                            name="itemNumber"
                            type="text"
                            label="Item No."
                            :disabled="!isEditing"
                        />
                    </div>
                    <div class="col-md-3">
                        <base-input
                            id="room"
                            v-model="preOrderUnitEdit.room"
                            name="room"
                            type="text"
                            label="Room"
                            :disabled="!isEditing"
                        />
                    </div>
                    <div class="col-md-3">
                        <div v-if="isEditing">
                            <div>
                                <label>Quantity</label>
                            </div>
                            <el-input-number
                                v-model="preOrderUnitEdit.quantity"
                                :min="1"
                            />
                        </div>
                        <base-input
                            v-else
                            id="quantity"
                            name="quantity"
                            type="text"
                            label="Quantity"
                            :value="preOrderUnitEdit.quantity"
                            disabled
                        />
                    </div>
                    <div class="col-md-3">
                        <base-input
                            name="date"
                            type="date"
                            label="Expected By"
                            :error="errors[0]"
                        >
                            <el-date-picker
                                v-model="preOrderUnitEdit.expectedAt"
                                :picker-options="{
                                    disabledDate: isDateInvalid,
                                }"
                                format="MM/dd/yyyy"
                                type="date"
                                placeholder="Select date"
                                :disabled="!isEditing"
                            />
                        </base-input>
                    </div>
                </div>
                <div v-if="isEditing" :class="$style.formActions">
                    <base-button
                        id="cancelEdit"
                        class="btn-fill mr-2"
                        @click="cancelEdit"
                    >
                        Cancel
                    </base-button>
                    <base-button
                        id="update"
                        :disabled="updating || isUnModified || invalid"
                        native-type="submit"
                        type="primary"
                        class="btn-fill"
                    >
                        Update
                    </base-button>
                </div>
            </form>
        </ValidationObserver>
    </card>
</template>
<script>
import _ from "lodash";
import MaskedInput from "vue-text-mask";
import { ValidationObserver } from "vee-validate";
import { numberMask } from "src/helpers/inputMasks";
import { DatePicker, InputNumber } from "element-ui";
import vSelect from "vue-select";
import moment from "moment-timezone";

export default {
    name: "PreOrderUnitDetailForm",
    components: {
        ValidationObserver,
        vSelect,
        MaskedInput,
        [DatePicker.name]: DatePicker,
        [InputNumber.name]: InputNumber,
    },
    props: {
        preOrderUnit: {
            type: Object,
            required: true,
        },
        manufacturers: {
            type: Array,
            required: true,
        },
        selectedManufacturer: {
            type: Object,
            required: true,
        },
        updating: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            numberMask,
            isEditing: false,
            preOrderUnitEdit: _.cloneDeep(this.preOrderUnit),
            searchText: null,
        };
    },
    computed: {
        isUnModified() {
            return _.isEqual(this.preOrderUnit, this.preOrderUnitEdit);
        },
        manufacturerOptions() {
            // include the original selected manufacturer, so that the name
            // is properly populated, otherwise it will come up as an id
            return [...this.manufacturers, this.selectedManufacturer];
        },
        length: {
            get() {
                return this.preOrderUnitEdit.length
                    ? String(this.preOrderUnitEdit.length)
                    : null;
            },
            set(value) {
                this.preOrderUnitEdit.length = parseInt(value, 10);
            },
        },
        width: {
            get() {
                return this.preOrderUnitEdit.width
                    ? String(this.preOrderUnitEdit.width)
                    : null;
            },
            set(value) {
                this.preOrderUnitEdit.width = parseInt(value, 10);
            },
        },
        height: {
            get() {
                return this.preOrderUnitEdit.height
                    ? String(this.preOrderUnitEdit.height)
                    : null;
            },
            set(value) {
                this.preOrderUnitEdit.height = parseInt(value, 10);
            },
        },
    },
    watch: {
        preOrderUnit(update) {
            this.preOrderUnitEdit = _.cloneDeep(update);
        },
        updating(val) {
            if (!val) this.isEditing = false;
        },
    },
    created() {
        this.preOrderUnitEdit.manufacturerId = this.selectedManufacturer?.id;
    },
    methods: {
        isDateInvalid(date) {
            const today = new Date(new Date().setHours(0, 0, 0, 0));
            return today > date;
        },
        formatDate(date) {
            if (date) {
                return moment(date).format("MM/DD/YYYY");
            }
            return "--";
        },
        formatTime(date) {
            if (date) {
                return moment(date).calendar();
            }
            return "--";
        },
        cancelEdit() {
            this.isEditing = false;
            this.preOrderUnitEdit = _.cloneDeep(this.preOrderUnit);
        },
        updateUnit() {
            this.$emit("updateUnit", this.preOrderUnitEdit);
        },
        search(search, loading) {
            this.searchText = search;
            this.$emit("searchManufacturers", { search, loading });
        },
    },
};
</script>

<style module>
.header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
}
.formActions {
    display: flex;
    justify-content: space-between;
}
</style>
